// =============================================================================
// BP-05-03: Calendar Frontend - API Client
// =============================================================================

import apiClient from '@/core/lib/api-client';
import type {
  Appointment,
  AppointmentSummary,
  AppointmentFormData,
  AppointmentUpdateData,
  AppointmentListResponse,
  CalendarQueryParams,
  CalendarDayResponse,
  CalendarWeekResponse,
  CalendarMonthResponse,
  AgendaResponse,
  AppointmentAttendee,
  ConflictCheckResult,
  CalendarExportOptions,
  RecurrenceEditScope,
  RecurrenceDeleteScope,
} from '../types';

// -----------------------------------------------------------------------------
// Base URL
// -----------------------------------------------------------------------------

// Note: apiClient already has /api/v1 as base URL
const APPOINTMENTS_BASE = '';

// -----------------------------------------------------------------------------
// Appointment CRUD
// -----------------------------------------------------------------------------

export const appointmentsApi = {
  /**
   * List appointments for a family with filters
   */
  list: async (
    familyId: string,
    params?: {
      page?: number;
      pageSize?: number;
      childId?: string;
      appointmentType?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<AppointmentListResponse> => {
    const response = await apiClient.get<AppointmentListResponse>(
      `${APPOINTMENTS_BASE}/families/${familyId}/appointments`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a single appointment by ID
   */
  get: async (appointmentId: string): Promise<Appointment> => {
    const response = await apiClient.get<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}`
    );
    return response.data;
  },

  /**
   * Create a new appointment
   */
  create: async (familyId: string, data: AppointmentFormData): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/families/${familyId}/appointments`,
      data
    );
    return response.data;
  },

  /**
   * Update an appointment
   */
  update: async (
    appointmentId: string,
    data: AppointmentUpdateData,
    recurrenceScope?: RecurrenceEditScope
  ): Promise<Appointment> => {
    const endpoint = recurrenceScope?.scope === 'thisAndFuture'
      ? `${APPOINTMENTS_BASE}/appointments/${appointmentId}/this-and-future`
      : recurrenceScope?.scope === 'this'
      ? `${APPOINTMENTS_BASE}/appointments/${appointmentId}/this-only`
      : `${APPOINTMENTS_BASE}/appointments/${appointmentId}`;

    const response = await apiClient.patch<Appointment>(endpoint, {
      ...data,
      instanceDate: recurrenceScope?.instanceDate,
    });
    return response.data;
  },

  /**
   * Delete an appointment
   */
  delete: async (
    appointmentId: string,
    recurrenceScope?: RecurrenceDeleteScope
  ): Promise<void> => {
    const endpoint = recurrenceScope?.scope === 'all'
      ? `${APPOINTMENTS_BASE}/appointments/${appointmentId}/delete-series`
      : `${APPOINTMENTS_BASE}/appointments/${appointmentId}`;

    await apiClient.delete(endpoint, {
      data: { instanceDate: recurrenceScope?.instanceDate },
    });
  },

  /**
   * Check for conflicts
   */
  checkConflicts: async (
    familyId: string,
    startsAt: string,
    endsAt: string,
    excludeAppointmentId?: string
  ): Promise<ConflictCheckResult> => {
    const response = await apiClient.post<ConflictCheckResult>(
      `${APPOINTMENTS_BASE}/families/${familyId}/appointments/check-conflicts`,
      { startsAt, endsAt, excludeAppointmentId }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Appointment Status Actions
// -----------------------------------------------------------------------------

export const appointmentActionsApi = {
  /**
   * Confirm an appointment
   */
  confirm: async (appointmentId: string): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/confirm`
    );
    return response.data;
  },

  /**
   * Cancel an appointment
   */
  cancel: async (appointmentId: string, reason?: string): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/cancel`,
      { reason }
    );
    return response.data;
  },

  /**
   * Mark appointment as completed
   */
  complete: async (
    appointmentId: string,
    notes?: string,
    followUpNeeded?: boolean,
    followUpNotes?: string
  ): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/complete`,
      { notes, followUpNeeded, followUpNotes }
    );
    return response.data;
  },

  /**
   * Reschedule an appointment
   */
  reschedule: async (
    appointmentId: string,
    startsAt: string,
    endsAt: string,
    recurrenceScope?: RecurrenceEditScope
  ): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/reschedule`,
      { startsAt, endsAt, ...recurrenceScope }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Recurrence Management
// -----------------------------------------------------------------------------

export const recurrenceApi = {
  /**
   * Set up recurrence for an appointment
   */
  setup: async (
    appointmentId: string,
    recurrenceRule: string,
    endDate?: string
  ): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/recurrence`,
      { recurrenceRule, endDate }
    );
    return response.data;
  },

  /**
   * Get all instances of a recurring appointment
   */
  getInstances: async (
    appointmentId: string,
    startDate: string,
    endDate: string
  ): Promise<AppointmentSummary[]> => {
    const response = await apiClient.get<AppointmentSummary[]>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/instances`,
      { params: { startDate, endDate } }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Attendees Management
// -----------------------------------------------------------------------------

export const attendeesApi = {
  /**
   * Add an attendee to an appointment
   */
  add: async (
    appointmentId: string,
    attendee: { email: string; name: string; role?: string }
  ): Promise<AppointmentAttendee> => {
    const response = await apiClient.post<AppointmentAttendee>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/attendees`,
      attendee
    );
    return response.data;
  },

  /**
   * Remove an attendee
   */
  remove: async (appointmentId: string, attendeeId: string): Promise<void> => {
    await apiClient.delete(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/attendees/${attendeeId}`
    );
  },

  /**
   * Send notifications to attendees
   */
  notify: async (appointmentId: string): Promise<void> => {
    await apiClient.post(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/notify`
    );
  },

  /**
   * Update attendee status (for RSVP)
   */
  updateStatus: async (
    appointmentId: string,
    attendeeId: string,
    status: string
  ): Promise<AppointmentAttendee> => {
    const response = await apiClient.patch<AppointmentAttendee>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/attendees/${attendeeId}`,
      { status }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Calendar Views
// -----------------------------------------------------------------------------

export const calendarApi = {
  /**
   * Get day view
   */
  getDay: async (familyId: string, date: string, childId?: string): Promise<CalendarDayResponse> => {
    const response = await apiClient.get<CalendarDayResponse>(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/day`,
      { params: { date, childId } }
    );
    return response.data;
  },

  /**
   * Get week view
   */
  getWeek: async (
    familyId: string,
    startDate: string,
    childId?: string
  ): Promise<CalendarWeekResponse> => {
    const response = await apiClient.get<CalendarWeekResponse>(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/week`,
      { params: { startDate, childId } }
    );
    return response.data;
  },

  /**
   * Get month view
   */
  getMonth: async (
    familyId: string,
    year: number,
    month: number,
    childId?: string
  ): Promise<CalendarMonthResponse> => {
    const response = await apiClient.get<CalendarMonthResponse>(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/month`,
      { params: { year, month, childId } }
    );
    return response.data;
  },

  /**
   * Get agenda view (list of upcoming appointments)
   */
  getAgenda: async (
    familyId: string,
    params?: {
      limit?: number;
      cursor?: string;
      childId?: string;
      startDate?: string;
    }
  ): Promise<AgendaResponse> => {
    const response = await apiClient.get<AgendaResponse>(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/agenda`,
      { params }
    );
    return response.data;
  },

  /**
   * Get appointments for a date range
   */
  getRange: async (
    familyId: string,
    params: CalendarQueryParams
  ): Promise<AppointmentSummary[]> => {
    const response = await apiClient.get<AppointmentSummary[]>(
      `${APPOINTMENTS_BASE}/families/${familyId}/appointments`,
      { params }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Export & Sync
// -----------------------------------------------------------------------------

export const calendarExportApi = {
  /**
   * Export calendar as iCal
   */
  exportIcal: async (familyId: string, options: CalendarExportOptions): Promise<Blob> => {
    const response = await apiClient.get(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/export/ical`,
      {
        params: {
          startDate: options.dateRange.start,
          endDate: options.dateRange.end,
          childId: options.childId,
        },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Export calendar as CSV
   */
  exportCsv: async (familyId: string, options: CalendarExportOptions): Promise<Blob> => {
    const response = await apiClient.get(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/export/csv`,
      {
        params: {
          startDate: options.dateRange.start,
          endDate: options.dateRange.end,
          childId: options.childId,
          includeNotes: options.includeNotes,
        },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Get Google Calendar sync URL
   */
  getGoogleSyncUrl: async (familyId: string): Promise<{ url: string }> => {
    const response = await apiClient.post<{ url: string }>(
      `${APPOINTMENTS_BASE}/families/${familyId}/calendar/sync/google`
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Prep Checklist & Questions
// -----------------------------------------------------------------------------

export const appointmentExtrasApi = {
  /**
   * Update prep checklist item
   */
  updateChecklistItem: async (
    appointmentId: string,
    itemId: string,
    completed: boolean
  ): Promise<Appointment> => {
    const response = await apiClient.patch<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/checklist/${itemId}`,
      { completed }
    );
    return response.data;
  },

  /**
   * Add checklist item
   */
  addChecklistItem: async (
    appointmentId: string,
    item: string
  ): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/checklist`,
      { item }
    );
    return response.data;
  },

  /**
   * Update question answer
   */
  updateQuestion: async (
    appointmentId: string,
    questionId: string,
    answer: string
  ): Promise<Appointment> => {
    const response = await apiClient.patch<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/questions/${questionId}`,
      { answer }
    );
    return response.data;
  },

  /**
   * Add question
   */
  addQuestion: async (
    appointmentId: string,
    question: string
  ): Promise<Appointment> => {
    const response = await apiClient.post<Appointment>(
      `${APPOINTMENTS_BASE}/appointments/${appointmentId}/questions`,
      { question }
    );
    return response.data;
  },
};

// -----------------------------------------------------------------------------
// Combined API Export
// -----------------------------------------------------------------------------

export const calendarApiClient = {
  appointments: appointmentsApi,
  actions: appointmentActionsApi,
  recurrence: recurrenceApi,
  attendees: attendeesApi,
  calendar: calendarApi,
  export: calendarExportApi,
  extras: appointmentExtrasApi,
};

export default calendarApiClient;
